import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFViewer, Image } from '@react-pdf/renderer';
import { Semester } from '../types/grades';
import { useUniversity } from '../contexts/UniversityContext';
import { calculateGPA, calculateCumulativeGPA } from '../utils/calculations';

// Import any icons or images if needed
// import universityLogo from '../assets/university-logo.png';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: 80,
    color: '#f0f0f0',
    opacity: 0.2,
    fontWeight: 'bold',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
    borderBottom: '2px solid #1e40af',
    paddingBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'extrabold',
    marginBottom: 10,
    color: '#1e40af',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
    marginBottom: 20,
  },
  overallProgress: {
    marginBottom: 40,
    padding: 20,
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  progressTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#1e40af',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  statBox: {
    flex: 1,
    margin: 8,
    padding: 15,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    textAlign: 'center',
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 6,
    fontWeight: 'medium',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e3a8a',
  },
  semesterTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 8,
    color: '#1e40af',
    borderLeft: '4px solid #1e40af',
  },
  table: {
    display: 'table',
    width: '100%',
    marginBottom: 25,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #e5e7eb',
    minHeight: 40,
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    fontWeight: 'bold',
    color: '#374151',
  },
  tableCell: {
    flex: 1,
    padding: 12,
    fontSize: 12,
    textAlign: 'center',
    color: '#4b5563',
  },
  summary: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 10,
    color: '#9ca3af',
    borderTop: '1px solid #e5e7eb',
    paddingTop: 10,
  },
  logo: {
    width: 100,
    height: 'auto',
    marginBottom: 10,
  },
});

interface PDFTranscriptProps {
  semesters: Semester[];
}

export function PDFTranscript({ semesters }: PDFTranscriptProps) {
  const { selectedUniversity } = useUniversity();
  const cumulativeGPA = calculateCumulativeGPA(semesters, selectedUniversity);
  
  // Calculate overall statistics
  const totalCredits = semesters.reduce((sum, semester) => 
    sum + semester.courses.reduce((credits, course) => credits + course.credits, 0), 0
  );
  const totalCourses = semesters.reduce((sum, semester) => 
    sum + semester.courses.length, 0
  );
  
  const getAcademicStanding = (gpa: number): string => {
    if (gpa >= 3.5) return 'Excellent';
    if (gpa >= 3.0) return 'Very Good';
    if (gpa >= 2.5) return 'Good';
    if (gpa >= 2.0) return 'Satisfactory';
    return 'Needs Improvement';
  };

  return (
    <PDFViewer style={{ width: '100%', height: '500px' }}>
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.watermark}>UNOFFICIAL</Text>
          
          <View style={styles.header}>
            {/* Add university logo if available */}
            {/* <Image src={universityLogo} style={styles.logo} /> */}
            <Text style={styles.title}>Official Academic Transcript</Text>
            <Text style={styles.subtitle}>{selectedUniversity.name}</Text>
          </View>

          <View style={styles.overallProgress}>
            <Text style={styles.progressTitle}>Overall Academic Progress</Text>
            
            <View style={styles.statsGrid}>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Cumulative GPA</Text>
                <Text style={styles.statValue}>{cumulativeGPA.toFixed(2)}</Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Total Credits</Text>
                <Text style={styles.statValue}>{totalCredits}</Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Total Courses</Text>
                <Text style={styles.statValue}>{totalCourses}</Text>
              </View>
            </View>
            
            <View style={styles.statsGrid}>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Academic Standing</Text>
                <Text style={styles.statValue}>
                  {getAcademicStanding(cumulativeGPA)}
                </Text>
              </View>
              <View style={styles.statBox}>
                <Text style={styles.statLabel}>Total Semesters</Text>
                <Text style={styles.statValue}>{semesters.length}</Text>
              </View>
            </View>
          </View>

          {semesters.map((semester) => (
            <View key={semester.id} wrap={false}>
              <Text style={styles.semesterTitle}>{semester.name}</Text>

              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Course Code</Text>
                  <Text style={styles.tableCell}>Credits</Text>
                  <Text style={styles.tableCell}>Grade</Text>
                  <Text style={styles.tableCell}>Points</Text>
                </View>

                {semester.courses.map((course, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={styles.tableCell}>{course.code}</Text>
                    <Text style={styles.tableCell}>{course.credits}</Text>
                    <Text style={styles.tableCell}>{course.grade}</Text>
                    <Text style={styles.tableCell}>
                      {(course.credits * calculateGPA([course], selectedUniversity).effectiveGPA).toFixed(2)}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.summary}>
                <Text>
                  Semester GPA: {calculateGPA(semester.courses, selectedUniversity).effectiveGPA.toFixed(2)}
                </Text>
              </View>
            </View>
          ))}
          
          <View style={styles.footer}>
            <Text>Generated on {new Date().toLocaleDateString()}</Text>
            <Text>This is an unofficial transcript for reference only</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
}